// src/hooks/useGardenEditor.ts

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../AuthProvider';
import {
    getPubGardens, getPlaceDetails, submitGardenToDatabase, deleteGarden,
    invalidatePubRaycastingCache, storeSubmitterName,
    updateGardenUncertainty, markPubHasNoGarden, submitGardenFeedback,
    approveGarden,
} from '../API';
import centroid from '@turf/centroid';
import type { Feature, Polygon } from 'geojson';
import MapboxDraw from '@mapbox/mapbox-gl-draw';

// --- TYPES ---
interface PolygonProperties {
    uid?: string;
    name?: string;
    is_owner?: boolean;
    is_uncertain?: boolean;
    centroid?: { type: 'Point'; coordinates: number[] };
}
type PolygonFeature = Feature<Polygon, PolygonProperties>;

interface ApiGardenObject {
    uid: string;
    name: string;
    sub_area_name?: string;
    garden_latlon: [number, number][];
    elevation: number;
    is_uncertain?: boolean;
    is_owner?: boolean;
}

export interface ExistingGarden {
    uid: string;
    name: string;
    polygon: [number, number][];
    elevation: number;
    is_uncertain?: boolean;
    is_owner?: boolean;
}

export type ActiveModal =
    | 'none' | 'update' | 'selectGarden' | 'editAction' | 'verified'
    | 'differentIssue' | 'name' | 'confirm' | 'submitterName'
    | 'submitAnother' | 'authSuccess' | 'login' | 'drawing-guide'
    | 'initial-prompt';

// --- THE HOOK ---
export const useGardenEditor = (
    googlePlaceId: string,
    isModerated: boolean,
    isEditing: boolean,
    onExitAndRefresh: () => void,
    onCancel: () => void
) => {
    const { user, profile, isAnonymous, refreshProfile } = useAuth();

    // State
    const [features, setFeatures] = useState<PolygonFeature[]>([]);
    const [viewState, setViewState] = useState({ longitude: -0.1278, latitude: 51.5074, zoom: 17 });
    const [pubName, setPubName] = useState<string>('Loading...');
    const [pubHasNoGarden, setPubHasNoGarden] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [activeModal, setActiveModal] = useState<ActiveModal>('none');
    const [showSidePanel, setShowSidePanel] = useState(true);
    const [isDrawing, setIsDrawing] = useState(false);
    const [initialModalSet, setInitialModalSet] = useState(false);

    // Edit tracking state
    const [selectedEditGarden, setSelectedEditGarden] = useState<ExistingGarden | null>(null);
    const [featureBeingEdited, setFeatureBeingEdited] = useState<PolygonFeature | null>(null);
    const [canUndo, setCanUndo] = useState(false);

    // Submission flow state
    const [featureToProcess, setFeatureToProcess] = useState<PolygonFeature | null>(null);
    const [pendingSubmission, setPendingSubmission] = useState<any>(null);

    const drawRef = useRef<MapboxDraw | null>(null);

    // --- MEMOIZED VALUES ---
    const gardensForSelection = useMemo<ExistingGarden[]>(() => {
        return features.map(f => ({
            uid: f.properties?.uid || String(f.id),
            name: f.properties?.name || 'Unnamed Garden',
            polygon: f.geometry.coordinates[0].map(c => [c[1], c[0]] as [number, number]),
            elevation: 0,
            is_uncertain: f.properties?.is_uncertain,
            is_owner: f.properties?.is_owner,
        }))
    }, [features]);

    const convertApiGardensToFeatures = useCallback((apiGardens: ApiGardenObject[]): PolygonFeature[] => {
        return apiGardens.map((garden) => {
            const polygonCoords = garden.garden_latlon.map(p => [p[1], p[0]]); // API [lat, lon] -> GeoJSON [lon, lat]
            const feature: PolygonFeature = {
                type: 'Feature',
                id: garden.uid,
                properties: {
                    uid: garden.uid,
                    name: garden.sub_area_name || garden.name,
                    is_owner: garden.is_owner,
                    is_uncertain: garden.is_uncertain,
                },
                geometry: { type: 'Polygon', coordinates: [polygonCoords] },
            };
            feature.properties!.centroid = centroid(feature).geometry;
            return feature;
        });
    }, []);

    // --- EFFECTS ---
    useEffect(() => {
        if (!googlePlaceId) return;
        setIsLoading(true);
        setError(null);

        const fetchData = async () => {
            try {
                const placeDetailsRes = await getPlaceDetails(googlePlaceId);
                let place = placeDetailsRes.data?.result || placeDetailsRes.data?.pubs?.[0];

                if (!place?.geometry?.location && !(place?.latitude && place?.longitude)) {
                    throw new Error("Could not find location for this pub.");
                }
                const lat = place.geometry?.location.lat || place.latitude;
                const lng = place.geometry?.location.lng || place.longitude;

                setPubName(place.name || 'Selected Pub');
                setPubHasNoGarden(place.has_no_garden || false);
                setViewState({ longitude: lng, latitude: lat, zoom: 18 });

                const gardensRes = await getPubGardens(googlePlaceId);
                const apiGardens = gardensRes.data ? Object.values(gardensRes.data) : [];
                setFeatures(convertApiGardensToFeatures(apiGardens as ApiGardenObject[]));
            } catch (err: any) {
                setError(err.message || "Could not load data for this pub.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [googlePlaceId, convertApiGardensToFeatures]);

    useEffect(() => {
        // This effect should only set the *initial* modal state when the component loads.
        if (isLoading || initialModalSet) return;

        // Determine initial state once loaded
        if (isModerated && !profile?.is_staff) {
            setActiveModal('verified');
            setShowSidePanel(false);
        } else if (isEditing && features.length > 0) {
            setActiveModal('update');
            setShowSidePanel(false);
        } else if (!isEditing && features.length === 0) {
            // Show initial prompt for new submissions when no gardens exist
            setActiveModal('initial-prompt');
            setShowSidePanel(false);
        } else {
            // This is for other cases
            setShowSidePanel(true);
        }
        setInitialModalSet(true); // Prevent this from running again and overwriting success modals
    }, [isLoading, isEditing, isModerated, features.length, profile?.is_staff, initialModalSet]);


    // --- ACTIONS ---
    const startDrawing = () => {
        drawRef.current?.deleteAll();
        drawRef.current?.changeMode('draw_polygon');
        setIsDrawing(true);
        setShowSidePanel(true);
        setActiveModal('drawing-guide');
        setCanUndo(false);
    };

    const cancelAction = () => {
        drawRef.current?.deleteAll();
        drawRef.current?.changeMode('simple_select');

        if (featureBeingEdited) {
            setFeatures(prev => [...prev, featureBeingEdited]);
            setFeatureBeingEdited(null);
        }

        setIsDrawing(false);
        setFeatureToProcess(null);
        setActiveModal('none');
        setSelectedEditGarden(null);
        
        onCancel();
    };

    const executeSubmission = async (payload: any) => {
        setIsSubmitting(true);
        setError(null);
        try {
            await submitGardenToDatabase(
                payload.googlePlaceId, payload.convertedCoordinates, payload.gardenName,
                payload.avgElevation, payload.isUncertain, payload.existingGardenUid,
                payload.submitterName || (profile?.display_name ?? 'Authenticated User')
            );
            await invalidatePubRaycastingCache(googlePlaceId);

            // Fetch the latest gardens to update the map internally
            const gardensRes = await getPubGardens(googlePlaceId);
            const apiGardens = gardensRes.data ? Object.values(gardensRes.data) : [];
            setFeatures(convertApiGardensToFeatures(apiGardens as ApiGardenObject[]));
            setIsDrawing(false);
            setFeatureToProcess(null);

            // Show the appropriate success modal
            if (!isAnonymous) {
                await refreshProfile();
                setActiveModal('authSuccess');
            } else {
                setActiveModal('submitAnother');
            }
        } catch (err: any) {
            setError(err.message || "Failed to save garden. Please try again.");
            setActiveModal('none');
        } finally {
            setIsSubmitting(false);
            setPendingSubmission(null);
        }
    };

    const handleFinalSubmit = async () => {
        if (!featureToProcess) return;
        setActiveModal('none');

        const coordinates = featureToProcess.geometry.coordinates[0];
        const submissionPayload = {
            googlePlaceId,
            convertedCoordinates: coordinates.map(p => [p[1], p[0], 0]), // lat, lon, elev
            gardenName: featureToProcess.properties?.name || 'Garden',
            avgElevation: 0,
            isUncertain: featureBeingEdited ? featureToProcess.properties?.is_uncertain || false : false,
            existingGardenUid: featureBeingEdited ? featureBeingEdited.id : null,
        };

        if (isAnonymous) {
            setPendingSubmission(submissionPayload);
            setActiveModal('submitterName');
        } else {
            await executeSubmission(submissionPayload);
        }
    };

    const modalActions = {
        closeAll: () => setActiveModal('none'),
        open: (modal: ActiveModal) => setActiveModal(modal),

        // Drawing / Submission Flow
        onDrawCreate: (newFeature: PolygonFeature) => {
            drawRef.current?.delete(String(newFeature.id));
            setFeatureToProcess(newFeature);
            setIsDrawing(false);
            setCanUndo(false);
            setActiveModal('name');
        },
        onDrawUpdate: (updatedFeature: PolygonFeature) => {
            // Don't do anything during update - wait for user to finish
            // The update event fires constantly during editing
        },
        onNameSubmit: (name: string) => {
            if (featureToProcess) {
                featureToProcess.properties = { ...featureToProcess.properties, name };
                setActiveModal('confirm');
            }
        },
        onConfirmSubmit: handleFinalSubmit,
        onAnonymousSubmit: async (name: string) => {
            if (pendingSubmission) {
                storeSubmitterName(name);
                await executeSubmission({ ...pendingSubmission, submitterName: name });
            }
        },

        // Edit Flow
        onPolygonClick: (featureId: string) => {
            const garden = gardensForSelection.find(g => g.uid === featureId);
            if (isModerated && !profile?.is_staff) {
                setActiveModal('verified');
                return;
            }
            const canEdit = profile?.is_staff || !isAnonymous || garden?.is_owner;
            if (garden && canEdit) {
                setSelectedEditGarden(garden);
                setActiveModal('editAction');
            }
        },
        onStartEditOutline: () => {
            const featureToEdit = features.find(f => f.id === selectedEditGarden?.uid);
            if (drawRef.current && featureToEdit) {
                setActiveModal('none');
                setFeatureBeingEdited(featureToEdit);
                setFeatures(prev => prev.filter(f => f.id !== selectedEditGarden?.uid));
                
                // Enable default controls for editing
                (drawRef.current as any).options.controls = {
                    point: false,
                    line_string: false,
                    polygon: false,
                    trash: true,
                    combine_features: false,
                    uncombine_features: false
                };
                
                drawRef.current.add(featureToEdit);
                drawRef.current.changeMode('direct_select', { featureId: String(featureToEdit.id) });
                setIsDrawing(true);
                setShowSidePanel(true);
            }
        },
        onToggleUncertainty: async () => {
            if (!selectedEditGarden) return;
            setIsSubmitting(true);
            try {
                await updateGardenUncertainty(googlePlaceId, {
                    uid: selectedEditGarden.uid,
                    name: selectedEditGarden.name,
                    garden_latlon: selectedEditGarden.polygon,
                    elevation: selectedEditGarden.elevation,
                    is_uncertain: selectedEditGarden.is_uncertain
                }, !selectedEditGarden.is_uncertain);
                await invalidatePubRaycastingCache(googlePlaceId);
                // After updating, just exit.
                onCancel();
            } catch (err: any) {
                setError(err.message || 'Failed to update garden.');
            } finally {
                setIsSubmitting(false);
                setActiveModal('none');
            }
        },
        onDelete: async () => {
            if (!selectedEditGarden) return;
            setIsSubmitting(true);
            try {
                await deleteGarden(selectedEditGarden.uid);
                await invalidatePubRaycastingCache(googlePlaceId);
                // After deleting, just exit. The parent will show the updated state.
                onCancel();
            } catch (err: any) {
                setError(err.message || 'Failed to delete garden.');
            } finally {
                setIsSubmitting(false);
                setActiveModal('none');
            }
        },
        onMarkNoGarden: async () => {
            setIsSubmitting(true);
            try {
                await markPubHasNoGarden(googlePlaceId);
                await invalidatePubRaycastingCache(googlePlaceId);
                onCancel(); // Exit editor on success
            } catch (err: any) {
                setError(err.message || 'Failed to update pub status.');
            } finally {
                setIsSubmitting(false);
            }
        },
        onFeedbackSubmit: async (type: 'outline' | 'elevation' | 'other', message?: string) => {
            return submitGardenFeedback(googlePlaceId, type, message ?? '');
        },
        onApprove: async () => {
            setIsSubmitting(true);
            try {
                await approveGarden(googlePlaceId);
                await invalidatePubRaycastingCache(googlePlaceId);
                onExitAndRefresh(); // Exit and refresh after approval
            } catch (err: any) {
                setError(err.message || 'Failed to approve garden.');
            } finally {
                setIsSubmitting(false);
                setActiveModal('none');
            }
        },

        // Drawing Controls Actions
        onActionable: (e: { actions: { undo: boolean } }) => {
            // Just pass through the draw state
            setCanUndo(e.actions.undo);
        },

        onUndo: () => {
            if (drawRef.current) {
                (drawRef.current as any).trash();
            }
        },
        onFinishDrawing: () => {
            // MapboxDraw will only create a feature if it's valid (>=3 points)
            // Otherwise, it just exits the mode and deletes the partial feature.
            drawRef.current?.changeMode('simple_select');
        },
        onFinishEditing: () => {
            if (drawRef.current && featureBeingEdited) {
                const allFeatures = drawRef.current.getAll();
                if (allFeatures.features.length > 0) {
                    const updatedFeature = allFeatures.features[0] as PolygonFeature;
                    drawRef.current?.changeMode('simple_select');
                    drawRef.current?.deleteAll();
                    setFeatureToProcess(updatedFeature);
                    setIsDrawing(false);
                    setCanUndo(false);
                    setActiveModal('confirm');
                }
            }
        },
        onCancelDrawing: () => {
            drawRef.current?.deleteAll();
            drawRef.current?.changeMode('simple_select');
            setIsDrawing(false);
            setCanUndo(false);
            
            // If there are existing gardens or the user started from the "Edit" flow,
            // go back to the "Update" modal.
            if (isEditing || features.length > 0) {
                setActiveModal('update');
            } else {
                // Otherwise, they were drawing the first garden. Cancel exits the editor.
                onCancel();
            }
        },
    };

    return {
        state: {
            isLoading, isSubmitting, error, pubName, pubHasNoGarden,
            viewState, features, isDrawing, showSidePanel,
            activeModal, profile, isAnonymous, selectedEditGarden, gardensForSelection,
            canUndo,
            featureBeingEdited,
        },
        actions: {
            drawRef,
            startDrawing,
            cancelAction,
            onExitAndRefresh,
            setSelectedEditGarden,
            setShowSidePanel,
            modal: modalActions
        },
    };
};